import { MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Layout, Menu } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import ImageWithBasePath from '@/core/data/img/ImageWithBasePath';

import useAuth from '../hooks/use-auth';

const { Header, Sider, Content } = Layout;

export interface RouteMeta {
  path: string;
  label: string;
  icon?: React.ReactNode;
  permissions?: string[];
  children?: RouteMeta[];
}

interface DashboardLayoutProps {
  routes: RouteMeta[];
  userPermissions?: string[];
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ routes, userPermissions = [] }) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true); // closed by default on mobile
    } else {
      setCollapsed(false); // open by default on desktop
    }
  }, [isMobile]);

  const toggleCollapsed = (): void => {
    setCollapsed(!collapsed);
  };

  const filteredRoutes = routes.filter(route => {
    if (!route.permissions) return true;
    return route.permissions.some(p => userPermissions.includes(p));
  });

  // only keep one submenu open
  const onOpenChange: MenuProps['onOpenChange'] = keys => {
    const latestKey = keys.find(key => !openKeys.includes(key));
    if (latestKey) {
      setOpenKeys([latestKey]);
    } else {
      setOpenKeys([]);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: 'logout', label: 'Logout', onClick: handleLogout },
  ];

  return (
    <Layout
      className={classNames('dashboard-layout', {
        'dashboard-mobile': isMobile,
        'dashboard-collapsed': collapsed && !isMobile,
      })}
    >
      {/* Sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed && !isMobile}
        collapsedWidth={isMobile ? 0 : 80}
        className={classNames('dashboard-sider', {
          overlay: isMobile && !collapsed,
        })}
        width={220}
      >
        <div className="brand">
          <NavLink to="/">
            <ImageWithBasePath
              src="assets/img/logo.png"
              alt="logo"
              width={100}
              height={42}
              isBackend={false}
            />
          </NavLink>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
        >
          {filteredRoutes.map(route =>
            route.children && route.children.length > 0 ? (
              <Menu.SubMenu key={route.label} icon={route.icon} title={route.label}>
                {route.children.map(child => (
                  <Menu.Item key={child.path} icon={child.icon}>
                    <NavLink to={child.path} onClick={() => isMobile && setCollapsed(true)}>
                      {child.label}
                    </NavLink>
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ) : (
              <Menu.Item key={route.path} icon={route.icon}>
                <NavLink to={route.path} onClick={() => isMobile && setCollapsed(true)}>
                  {route.label}
                </NavLink>
              </Menu.Item>
            )
          )}
        </Menu>
      </Sider>

      {/* Backdrop for mobile */}
      {isMobile && !collapsed && (
        <div
          className={classNames('dashboard-backdrop', { active: !collapsed })}
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* Main Layout */}
      <Layout>
        <Header className="dashboard-header">
          <div className="left">
            {React.createElement(collapsed && !isMobile ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: toggleCollapsed,
            })}
          </div>
          <div className="right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar icon={<UserOutlined />} className="avatar" />
            </Dropdown>
          </div>
        </Header>
        <Content className="dashboard-content" style={{ marginLeft: collapsed ? '80px' : '220px' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
